import { useRouter } from 'next/router';
import { Fragment, useContext, useMemo, useState } from 'react';
import Button from './Button';
import Dialog from './Dialog';
import Flex from './Flex';
import FormControl from './FormControl';
import Input from './Input';
import Select from './Select';
import countryCode from '@/constants/countryCode';
import AuthContext from '@/contexts/auth';
import FeedbackContext from '@/contexts/feedback';
import SchoolContext from '@/contexts/school';
import { useLogout } from '@/hooks/useLogout';
import useMFA from '@/hooks/useMFA';

type Option = {
  value: string | number;
  label: string;
};

/**
 * Enroll MFA Component
 * @returns
 */
export const EnrollMFA = ({ setVisible }: { setVisible: (visible: boolean) => void }) => {
  const [showVerification, setShowVerification] = useState<boolean>();
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [verificationCode, setVerificationCode] = useState<string>('');
  const { logout } = useLogout();
  const { toggleProcessing, showToast } = useContext(FeedbackContext);
  const { setLoginRequired } = useContext(AuthContext);

  // SMS verify
  const { enrollSendSMS, verifyEnrollMFA } = useMFA();

  /**
   * Send Enroll SMS verification code
   */
  const onEnroll = async () => {
    try {
      const _phone = countryCodeSelect + phoneNumber;
      await enrollSendSMS(_phone);
      setShowVerification(true);
    } catch (error: any) {
      if (error.code == 'auth/requires-recent-login') {
        setLoginRequired(true);
      } else {
        showToast({ status: 'error', message: '保存できませんでした。再度操作してください。' });
      }
    }
  };

  /**
   * Verify OTP for enroment
   */
  const onVerifyEnroll = async () => {
    if (verificationCode) {
      try {
        toggleProcessing();
        await verifyEnrollMFA(verificationCode);
        showToast({ status: 'success', message: '電話番号が確認されました。' });
        setVisible(false);
      } catch (error) {
        logout();
        showToast({ status: 'error', message: '電話番号が確認できませんでした。。' });
      } finally {
        toggleProcessing();
      }
    }
  };

  // Contry code

  const [countryCodeSelect, setCountryCodeSelect] = useState<string>('');
  /**
   * Generate country code options
   * @returns list of country code options
   */
  const countryCodeOptions = (): Option[] => {
    const options = [
      {
        value: '',
        label: '',
      },
    ];

    countryCode.forEach((item) => {
      options.push({
        value: item.dial_code,
        label: `${item.dial_code} ${item.name}(${item.code})`,
      });
    });

    return options;
  };
  return (
    <Dialog visible={true} title='電話番号設定'>
      <Dialog.Body
        text={
          <Fragment>
            {!showVerification /** * Introduce phone number to send SMS verification code */ ? (
              <Flex gap={20} type='column'>
                <div className='uk-width-1-1  uk-flex uk-flex-left uk-flex-bottom'>
                  <div className='uk-width-1-3'>
                    <FormControl label='国'>
                      <Select options={countryCodeOptions()} onChange={(e) => setCountryCodeSelect(e.target.value)} />
                    </FormControl>
                  </div>
                  <div className='uk-width-2-3 uk-margin-small-left'>
                    <FormControl label='電話番号'>
                      <Input
                        onChange={(e) => {
                          setPhoneNumber(e.target.value);
                        }}
                        type='text'
                        placeholder='8022223333'
                        autoComplete='off'
                        aria-label='電話番号'
                      />
                    </FormControl>
                  </div>
                </div>

                <div className='uk-width-1-1 uk-flex uk-flex-right'>
                  <div>
                    <Button
                      type='button'
                      size='lg'
                      variant='outlined'
                      handleClick={() => {
                        setVisible(false);
                      }}>
                      キャンセル
                    </Button>
                  </div>

                  <div className='uk-margin-small-left'>
                    <Button
                      disabled={!countryCodeSelect || !phoneNumber}
                      type='button'
                      size='lg'
                      variant='primary'
                      handleClick={() => {
                        onEnroll();
                      }}>
                      送信する
                    </Button>
                  </div>
                </div>
              </Flex>
            ) : (
              /** * Introduce verification code */
              <Flex gap={20} type='column'>
                <FormControl label='確認コード'>
                  <Input
                    onChange={(e) => {
                      setVerificationCode(e.target.value);
                    }}
                    type='text'
                    autoComplete='off'
                    aria-label=''
                    value={verificationCode}
                  />
                </FormControl>
                <div className='uk-flex uk-flex-right'>
                  <Button
                    type='button'
                    size='lg'
                    variant='outlined'
                    handleClick={() => {
                      setVisible(false);
                    }}>
                    キャンセル
                  </Button>
                  <div className='uk-margin-small-left'>
                    <Button
                      disabled={!verificationCode}
                      type='button'
                      size='lg'
                      variant='primary'
                      handleClick={() => {
                        onVerifyEnroll();
                      }}>
                      送信する
                    </Button>
                  </div>
                </div>
              </Flex>
            )}
            {useMemo(() => {
              return <div id='recaptcha-enroll'></div>;
            }, [])}
          </Fragment>
        }
      />
    </Dialog>
  );
};

/**
 * Login MFA Component
 * @returns
 */
export const LoginMFA = () => {
  const [showVerification, setShowVerification] = useState<boolean>();
  const [verificationCode, setVerificationCode] = useState<string>('');
  const { logout } = useLogout();
  const { toggleProcessing, showToast } = useContext(FeedbackContext);
  const { setLoginError, loginError } = useContext(AuthContext);
  const { slug } = useContext(SchoolContext);
  const router = useRouter();

  const phoneNumber = useMemo(() => {
    if (loginError) {
      return loginError.customData?._serverResponse?.mfaInfo?.[0]?.phoneInfo;
    }
  }, [loginError]);

  // SMS verify
  const { verifyLoginMFA, loginSendSMS } = useMFA();

  /**
   * Send Enroll SMS verification code
   */
  const onSendSMS = async () => {
    try {
      await loginSendSMS();
      setShowVerification(true);
    } catch (error) {
      logout();
      setLoginError(undefined);

      setShowVerification(false);
      showToast({
        status: 'error',
        message: 'お客様の電話番号に確認コードを送信することはできません。 もう一度ログインしてください！',
      });
    }
  };

  /**
   * Verify OTP for enroment
   */
  const onVerifyLogin = async () => {
    if (verificationCode) {
      try {
        toggleProcessing();
        await verifyLoginMFA(verificationCode);
        showToast({ status: 'success', message: '電話番号が確認されました。' });
        if (router.query.redirect) {
          router.push(`/${slug}/${router.query.redirect}`);
        } else {
          router.push(`/${slug}`);
        }
      } catch (error) {
        showToast({ status: 'error', message: '確認コードが正しくありません。もう一度お試しください。' });
      } finally {
        toggleProcessing();
        setLoginError(undefined);
      }
    }
  };

  return (
    <Dialog visible={true} title='二段階認証'>
      <Dialog.Body
        text={
          <Fragment>
            {!showVerification ? (
              /**
               * Introduce phone number to send SMS verification code
               */
              <Flex gap={20} type='column'>
                <div>電話番号の {phoneNumber} に確認コードを送信します</div>
                <div className='uk-width-1-1'>
                  <Button
                    type='button'
                    size='lg'
                    variant='primary'
                    handleClick={() => {
                      onSendSMS();
                    }}>
                    送信する
                  </Button>
                </div>
              </Flex>
            ) : (
              /**
               * Introduce verification code
               */
              <Flex gap={20} type='column'>
                <FormControl label='確認コード'>
                  <Input
                    onChange={(e) => {
                      setVerificationCode(e.target.value);
                    }}
                    type='text'
                    autoComplete='off'
                    aria-label='１枚あたり'
                    value={verificationCode}
                  />
                </FormControl>
                <div className='uk-width-1-1'>
                  <Button
                    disabled={!verificationCode}
                    type='button'
                    size='lg'
                    variant='primary'
                    handleClick={() => {
                      onVerifyLogin();
                    }}>
                    確認する
                  </Button>
                </div>
              </Flex>
            )}
            {useMemo(() => {
              return <div id='recaptcha-login'></div>;
            }, [])}
          </Fragment>
        }
      />
    </Dialog>
  );
};
